import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useParams } from "react-router";

import { useAuth } from "@/providers";
import { Page } from "@/shared/components";
import { LoadingScreen } from "@/shared/components/LoadingScreen";
import {
  getCredentialRequestQueryKey,
  useApproveCredentialRequest,
  useCredentialRequest,
  useDenyCredentialRequest,
} from "@/shared/domain/tokens/hooks";

import styles from "./CredentialRequestApproval.module.css";
import { CredentialRequestCard } from "./CredentialRequestCard";
import {
  CredentialRequestOutcome,
  type CredentialRequestOutcomeState,
} from "./CredentialRequestOutcome";
import { CredentialRequestReview } from "./CredentialRequestReview";
import { isLoopbackCallbackUrl } from "./loopbackCallback";

const UNAVAILABLE_MESSAGE =
  "Roboto couldn't load this sign-in. A sign-in can be approved or denied once, within 10 minutes of starting it. Start a new sign-in from your terminal.";

const DECISION_FAILED_MESSAGE =
  "Roboto couldn't confirm your answer, and this page can't check whether it went through. Press Ctrl-C in your terminal, then start a new sign-in.";

export interface CredentialRequestApprovalProps {
  /** Navigate to a validated CLI callback URL; defaults to `window.location.assign`. */
  leaveFor?: (url: string) => void;
}

function navigateAway(url: string) {
  window.location.assign(url);
}

/**
 * Let the signed-in user approve or deny a CLI credential request.
 *
 * Approval delivers a one-time authorization code through a validated loopback callback or
 * terminal-paste instructions. The CLI redeems it for an access token that this page never receives.
 */
export function CredentialRequestApproval({
  leaveFor = navigateAway,
}: CredentialRequestApprovalProps) {
  const { credentialRequestId: routeRequestId } = useParams();
  const credentialRequestId = routeRequestId ?? "";
  return (
    <RequestApproval
      key={credentialRequestId}
      credentialRequestId={credentialRequestId}
      leaveFor={leaveFor}
    />
  );
}

function RequestApproval({
  credentialRequestId,
  leaveFor,
}: {
  credentialRequestId: string;
  leaveFor: (url: string) => void;
}) {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const credentialRequest = useCredentialRequest(credentialRequestId);
  const approve = useApproveCredentialRequest();
  const deny = useDenyCredentialRequest();
  const [outcome, setOutcome] = React.useState<CredentialRequestOutcomeState>();
  // Prevent competing clicks before mutation state disables the controls on the next render.
  const decisionInFlight = React.useRef(false);

  // Keep the review while mounted to avoid refetching a decided request. Remove it on departure
  // so revisiting asks the server whether the request is still pending.
  React.useEffect(
    () => () =>
      queryClient.removeQueries({
        queryKey: getCredentialRequestQueryKey(credentialRequestId),
      }),
    [queryClient, credentialRequestId],
  );

  const onApprove = React.useCallback(() => {
    if (decisionInFlight.current) {
      return;
    }
    decisionInFlight.current = true;
    approve.mutate(credentialRequestId, {
      onSettled: () => {
        decisionInFlight.current = false;
      },
      onSuccess: (approval) => {
        if (isLoopbackCallbackUrl(approval.redirect_url)) {
          setOutcome({ kind: "returnedToCli" });
          leaveFor(approval.redirect_url);
        } else {
          setOutcome({
            kind: "pasteCode",
            authorizationCode: approval.authorization_code,
          });
        }
      },
    });
  }, [approve, leaveFor, credentialRequestId]);

  const onDeny = React.useCallback(() => {
    if (decisionInFlight.current) {
      return;
    }
    decisionInFlight.current = true;
    deny.mutate(credentialRequestId, {
      onSettled: () => {
        decisionInFlight.current = false;
      },
      onSuccess: (denial) => {
        setOutcome({ kind: "denied" });
        if (isLoopbackCallbackUrl(denial.redirect_url)) {
          leaveFor(denial.redirect_url);
        }
      },
    });
  }, [deny, leaveFor, credentialRequestId]);

  let content: React.ReactNode;
  if (outcome !== undefined) {
    content = <CredentialRequestOutcome outcome={outcome} />;
  } else if (credentialRequest.isPending) {
    content = <LoadingScreen />;
  } else if (credentialRequest.data === undefined) {
    content = (
      <CredentialRequestCard title="Sign-in unavailable">
        <p className={styles.body}>{UNAVAILABLE_MESSAGE}</p>
      </CredentialRequestCard>
    );
  } else {
    content = (
      <CredentialRequestReview
        credentialRequest={credentialRequest.data}
        signedInAs={currentUser?.user_id}
        onApprove={onApprove}
        onDeny={onDeny}
        isDeciding={approve.isPending || deny.isPending}
        // Each decision is sent once. If it went through but its response was lost, the request is
        // no longer pending, so its authorization code or redirect URL cannot be fetched again, and
        // the CLI waits for an answer that never arrives until the user stops it.
        errorMessage={
          approve.isError || deny.isError ? DECISION_FAILED_MESSAGE : undefined
        }
      />
    );
  }

  return (
    <Page title={"Roboto - Approve CLI Sign-In"}>
      <div className={styles.page}>{content}</div>
    </Page>
  );
}
